import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import Cookies from 'js-cookie';
import cookie from 'js-cookie';
import { useRouter } from 'next/router';
import {
  useContext,
  createContext,
  useState,
  useEffect,
  FunctionComponent,
  ReactNode,
} from 'react';

import tenantConfig from '../../../../config/tenants';
import useLoggedUser from '../../../hooks/use-logged-user';
import { fetchUserShippingLocation } from '../../../services/shipping-locations/list';
import { reportRequestFailure } from '../../../utils/network-error';
import {
  marktingAtom,
  preferencesAtom,
  statisticsAtom,
  useUpdateConcentAtom,
  requiredAtom,
} from '../../../state/application';
import { SHIPPING_LOCATION, IS_PSA, SERVICEBOX_COOKIE } from '../../../static/cookies';
import { GLOBAL_BUSINESS_VARIABLES } from './business';

const detailPricesShippingLocations = [
  1, // Portugal - Continental
  2, // Portugal - Açores
  3, // Portugal - Madeira
  4, // Spain - España - Peninsular
  5, // Spain - España - Islas Baleares
  6, // Spain - España - Islas Canarias
  11, // France - Métropolitaine
  13, // Germany - Deutschland
  23, // Italy - Italia Continentale / Peninsulare
];

const getIsPSAState = () => {
  const sbCookie = Cookies.get(SERVICEBOX_COOKIE);
  if (sbCookie) {
    try {
      const parsed = JSON.parse(decodeURIComponent(sbCookie));
      if (typeof parsed.is_psa !== 'undefined') {
        return !!parsed.is_psa;
      }
    } catch (e) {
      if (sbCookie.includes('is_psa=')) {
        return sbCookie.includes('is_psa=true');
      }
    }
  }
  return Cookies.get(IS_PSA) === 'true';
};


const appContext = createContext<{
  // features
  features: {
    isBrandEnabled: boolean;
  };
  isKBA: boolean;
  isPlate: boolean;
  shippingLocation: number | null;
  detailedPrice: boolean | null;
  concent: {
    preferences: boolean | null;
    markting: boolean | null;
    statistics: boolean | null;
    required: boolean | null;
  };
  tenantConfig: Tenant;
  isBparts: boolean;
  brand?: string;
  brandName?: string;
  isPSA: boolean;
  isBot: boolean;
  businessValues: any;
}>({
  features: {
    isBrandEnabled: !!process.env.NEXT_PUBLIC_BRAND_SLUG,
  },
  isKBA: false,
  isPlate: false,
  shippingLocation: null,
  detailedPrice: null,
  concent: {
    preferences: false,
    markting: false,
    statistics: false,
    required: false,
  },
  tenantConfig: tenantConfig,
  isBparts: false,
  brand: process.env.NEXT_PUBLIC_BRAND_SLUG || null,
  brandName: process.env.NEXT_PUBLIC_BRAND_NAME || null,
  isPSA: getIsPSAState(),
  isBot: false,
  businessValues: GLOBAL_BUSINESS_VARIABLES,
});

// Italy shipping location id: plate search is only allowed for PRO users
// or the ServiceBox tenant (id 5).
export const ITALY_SHIPPING_LOCATION_ID = 23;

const getIsKBA = (shippingLocation: number) => shippingLocation === 13;
const getIsPlate = (
  shippingLocation: number,
  user: ReturnType<typeof useLoggedUser>,
  tenant: Tenant
) => {
  if (shippingLocation === ITALY_SHIPPING_LOCATION_ID) {
    return Boolean(user?.staff?.is_pro) || tenant?.id == 5;
  }

  return (
    [1, 2, 3, 4, 5, 6, 8, 11, 14, 15, 19, 22, 24, 26, 28].indexOf(
      shippingLocation
    ) >= 0
  );
};
const validDetailPricingLocations = (shippingLocation: number) =>
  detailPricesShippingLocations.includes(shippingLocation);

export const AppContextProvider: FunctionComponent<{
  shippingLocation: number;
  detailPrices?: string;
  isPSA?: boolean;
  isBot?: boolean;
  children: ReactNode;
}> = ({ shippingLocation, detailPrices, isPSA: ssrIsPSA, isBot, children }) => {
  const { locale = 'en' } = useRouter();
  const [shipping, setShipping] = useState(shippingLocation);
  const showShippingCostCookie = validDetailPricingLocations(shippingLocation)
    ? Cookies.get('detail_prices') || detailPrices
    : null;
  const preferences = useAtomValue(preferencesAtom);
  const markting = useAtomValue(marktingAtom);
  const statistics = useAtomValue(statisticsAtom);
  const required = useAtomValue(requiredAtom);
  const triggerConcentUppdated = useUpdateConcentAtom();

  const client = useQueryClient();
  const user = useLoggedUser();

  useEffect(() => {
    triggerConcentUppdated();
    if (!shipping) {
      const clientShipping = parseInt(cookie.get(SHIPPING_LOCATION) || '0', 10);

      if (!clientShipping) {
        client
          .fetchQuery(['user-shipping', locale], ({ queryKey: [, lng] }) =>
            fetchUserShippingLocation({ locale: lng })
          )
          // o `catch` fica ANTES do `.then` de propósito: engole só a falha do
          // pedido — que já tem fallback no `clientShipping` — enquanto um erro
          // dentro do `.then` continua a subir, porque aí é bug nosso.
          .catch((error) => {
            reportRequestFailure('shipping_locations:selected', error);
            return undefined;
          })
          .then((data) => {
            if (data) {
              setShipping(data.id);
            }
          });

        // TODO PROBABLY MAKE ERROR IF NO SHIPPING LOCATION
      }
      setShipping(clientShipping);
    }
  }, []);

  return (
    <appContext.Provider
      value={{
        features: {
          isBrandEnabled: !!process.env.NEXT_PUBLIC_BRAND_SLUG,
        },
        isKBA: getIsKBA(shipping),
        isPlate: getIsPlate(shipping, user, tenantConfig),
        shippingLocation: shipping,
        detailedPrice: showShippingCostCookie
          ? showShippingCostCookie === 'true'
          : null,
        concent: {
          preferences,
          markting,
          statistics,
          required,
        },
        tenantConfig: tenantConfig,
        isBparts: process.env.NEXT_PUBLIC_TENANT === '1',
        brand: process.env.NEXT_PUBLIC_BRAND_SLUG,
        brandName: process.env.NEXT_PUBLIC_BRAND_NAME,
        isPSA: typeof ssrIsPSA !== 'undefined' ? ssrIsPSA : getIsPSAState(),
        isBot: !!isBot,
        businessValues: GLOBAL_BUSINESS_VARIABLES,
      }}
    >
      {children}
    </appContext.Provider>
  );
};

export const useAppContext = () => useContext(appContext);
